import { useQuery, useQueryClient } from "@tanstack/react-query"
import { Fragment, useMemo, useState } from "react"
import { ChevronRight, Plus } from "lucide-react"
import { api } from "@/lib/api"
import { PageHeader } from "@/components/portal/PageHeader"
import { FilterBar } from "@/components/portal/FilterBar"
import { Loading, ErrorBox } from "@/components/portal/LoadingError"
import { toast } from "@/components/portal/Toast"
import { useTenantFilter } from "@/hooks/useTenantFilter"
import { cn } from "@/lib/utils"

/** "2026-07-21T10:17" -> "21.07.2026 10:17" (faellt auf den Rohwert zurueck). */
function fmtDateTime(iso: string): string {
  if (!iso) return "—"
  const [d, t] = iso.split("T")
  const p = d?.split("-")
  if (!p || p.length !== 3) return iso
  return `${p[2]}.${p[1]}.${p[0]}${t ? ` ${t.slice(0, 5)}` : ""}`
}

/** Vorgaenge gruppiert nach Tenant — internes Ticketsystem, Detail per Klick. */
export default function TicketsList() {
  const [tenant, setTenant] = useTenantFilter()
  const [from, setFrom] = useState("")
  const [to, setTo] = useState("")
  const [createOpen, setCreateOpen] = useState(false)
  const [openId, setOpenId] = useState<string | null>(null)
  const { data, isLoading, error, isFetching, refetch } = useQuery({
    queryKey: ["tickets", tenant],
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/tickets", {
        params: { query: { tenant: tenant || undefined } },
      })
      if (error) throw new Error(String(error))
      return data
    },
    staleTime: 1000 * 30,
  })

  // Client-side Datums-Filter
  const filteredGroups = useMemo(() => {
    if (!data) return []
    if (!from && !to) return data.groups
    const fromTs = from ? new Date(from).getTime() : 0
    const toTs = to ? new Date(to).getTime() + 24 * 3600_000 : Infinity
    return data.groups.map((g) => ({
      ...g,
      tickets: g.tickets.filter((t) => {
        // Nur der Tagesanteil zaehlt — created_at traegt jetzt auch die Uhrzeit,
        // sonst wuerde der Bis-Filter den letzten Tag teilweise abschneiden.
        const tag = (t.created_at || "").slice(0, 10)
        const ts = tag ? new Date(tag).getTime() : 0
        return ts >= fromTs && ts <= toTs
      }),
    })).filter((g) => g.tickets.length > 0)
  }, [data, from, to])

  // Monats-Statistiken kommen als COUNT vom Backend, NICHT aus `data.groups`:
  // die Liste ist je Tenant gedeckelt, gezaehlt wird aber ueber den gesamten
  // Bestand. Clientseitiges Zaehlen hatte aeltere Monate still zu niedrig
  // angezeigt.
  const monthStats = useMemo<[string, number][]>(() => {
    if (!data?.month_counts) return []
    return data.month_counts
      .map((m) => [m.month, m.count] as [string, number])
      .sort((a, b) => a[0].localeCompare(b[0]))
      .slice(-6)
  }, [data])

  if (error) return <div className="p-10"><ErrorBox error={error} /></div>

  return (
    <div className="px-10 py-8 max-w-[1240px]">
      <PageHeader title="Vorgänge" count={data?.total} onRefresh={() => refetch()} refreshing={isFetching}>
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          className="inline-flex items-center gap-1 text-[12.5px] font-medium px-3 py-1.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90"
        >
          <Plus className="w-3.5 h-3.5" /> Neues Ticket
        </button>
      </PageHeader>
      <FilterBar tenant={tenant} onTenantChange={setTenant}>
        <label className="text-[11.5px] text-muted-foreground flex items-center gap-1">
          Von
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="pp-input pp-input-sm" />
        </label>
        <label className="text-[11.5px] text-muted-foreground flex items-center gap-1">
          Bis
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="pp-input pp-input-sm" />
        </label>
        {(from || to) && (
          <button className="pp-btn pp-btn-sm" onClick={() => { setFrom(""); setTo("") }}>
            Reset
          </button>
        )}
      </FilterBar>

      {monthStats.length > 0 && (
        <div className="flex gap-2 mb-4 flex-wrap">
          {monthStats.map(([month, count]) => (
            <div key={month} className="px-3 py-2 rounded-md bg-card border text-[12px]">
              <div className="text-[10.5px] text-muted-foreground uppercase">{month}</div>
              <div className="text-[16px] font-semibold">{count}</div>
            </div>
          ))}
        </div>
      )}

      {isLoading ? (
        <Loading label="Lädt Vorgänge..." />
      ) : filteredGroups.length === 0 ? (
        <div className="p-10 text-center text-muted-foreground rounded-lg bg-card border">
          Keine Vorgänge gefunden.
        </div>
      ) : (
        <div className="space-y-5">
          {filteredGroups.map((g) => (
            <section key={g.tenant_slug} className="rounded-lg border bg-card overflow-hidden shadow-xs">
              <div className="px-4 py-2.5 bg-secondary border-b flex items-center justify-between">
                <h2 className="text-[14px] font-semibold">
                  {g.tenant_name}
                  <span className="ml-2 text-[11px] text-muted-foreground">({g.tenant_slug})</span>
                </h2>
                <span className="text-[11px] bg-card px-2 py-0.5 rounded border">
                  {g.total > g.tickets.length
                    ? `${g.tickets.length} von ${g.total}`
                    : g.total}
                </span>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-card border-b">
                    <th className="px-4 py-2 text-[10.5px] font-semibold uppercase text-muted-foreground text-left w-24">Erstellt</th>
                    <th className="px-2 py-2 w-8"></th>
                    <th className="px-4 py-2 text-[10.5px] font-semibold uppercase text-muted-foreground text-left">Betreff</th>
                    <th className="px-4 py-2 text-[10.5px] font-semibold uppercase text-muted-foreground text-left w-24">Prio</th>
                    <th className="px-4 py-2 text-[10.5px] font-semibold uppercase text-muted-foreground text-left w-32">Quelle</th>
                  </tr>
                </thead>
                <tbody>
                  {g.tickets.map((t) => (
                    <Fragment key={t.id}>
                    <tr
                      className="border-b last:border-0 hover:bg-secondary/40 cursor-pointer"
                      onClick={() => setOpenId(openId === t.id ? null : t.id)}
                    >
                      <td className="px-4 py-2 text-[12px] text-muted-foreground whitespace-nowrap">
                        {fmtDateTime(t.created_at)}
                      </td>
                      <td className="px-2 py-2">
                        <ChevronRight
                          className={cn("w-4 h-4 text-muted-foreground transition-transform",
                                        openId === t.id && "rotate-90 text-primary")}
                        />
                      </td>
                      <td className="px-4 py-2 text-[13px]">{t.subject}</td>
                      <td className="px-4 py-2">
                        <span
                          className={cn(
                            "inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold",
                            t.priority === "HIGH" ? "bg-destructive/10 text-destructive" :
                            t.priority === "MEDIUM" ? "bg-amber-500/10 text-amber-700" :
                            "text-muted-foreground",
                          )}
                        >
                          {t.priority || "—"}
                        </span>
                      </td>
                      <td className="px-4 py-2">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-secondary text-muted-foreground">
                          {t.source_label}
                        </span>
                      </td>
                    </tr>
                    {openId === t.id && (
                      <tr className="border-b last:border-0 bg-secondary/20">
                        <td colSpan={5} className="px-4 py-3">
                          {t.content ? (
                            // Vorgangs-Text 1:1 anzeigen — er ist bewusst
                            // zeilenweise aufgebaut (Kunde, Termin, Monteur,
                            // HERO-IDs, Zusammenfassung).
                            <pre className="whitespace-pre-wrap break-words font-sans text-[12.5px] leading-relaxed text-foreground">
                              {t.content}
                            </pre>
                          ) : (
                            <p className="text-[12.5px] text-muted-foreground italic">
                              Keine Details hinterlegt.
                            </p>
                          )}
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </section>
          ))}
        </div>
      )}

      <CreateTicketModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        defaultTenant={tenant}
      />
    </div>
  )
}

function CreateTicketModal({
  open, onClose, defaultTenant,
}: { open: boolean; onClose: () => void; defaultTenant: string }) {
  const qc = useQueryClient()
  const tenantsDd = useQuery({
    queryKey: ["tenants-dropdown"],
    queryFn: async () => (await api.GET("/api/v1/tenants", {})).data,
    staleTime: 1000 * 60 * 5,
    enabled: open,
  })
  const [tenantSlug, setTenantSlug] = useState(defaultTenant)
  const [subject, setSubject] = useState("")
  const [description, setDescription] = useState("")
  const [priority, setPriority] = useState<"LOW" | "MEDIUM" | "HIGH">("MEDIUM")
  const [submitting, setSubmitting] = useState(false)

  if (!open) return null

  const submit = async () => {
    if (!subject.trim()) {
      toast("Betreff ist erforderlich", "error")
      return
    }
    setSubmitting(true)
    try {
      const r = await fetch("/admin/tickets/create", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tenant_slug: tenantSlug, subject, description, priority }),
      })
      const d = await r.json()
      if (d.ok) {
        toast(`✓ Vorgang #${d.ticket_id} erstellt`)
        qc.invalidateQueries({ queryKey: ["tickets"] })
        setSubject(""); setDescription(""); setPriority("MEDIUM")
        onClose()
      } else {
        toast(d.error || "Fehler beim Erstellen", "error")
      }
    } catch (e) {
      toast("Netzwerkfehler: " + (e instanceof Error ? e.message : String(e)), "error")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div
      className="pp-overlay"
      data-portal-modal
      style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.5)",
               display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          background: "var(--bg-card)", borderRadius: 12, boxShadow: "0 20px 60px rgba(0,0,0,.25)",
          width: "100%", maxWidth: 480, padding: 24,
        }}
      >
        <h3 className="text-[17px] font-semibold mb-4">Neuer Vorgang</h3>
        <div className="flex flex-col gap-3">
          <div>
            <label className="text-[11.5px] font-semibold text-muted-foreground">Tenant</label>
            <select
              value={tenantSlug}
              onChange={(e) => setTenantSlug(e.target.value)}
              className="pp-input w-full"
            >
              <option value="">— Kein Tenant —</option>
              {tenantsDd.data?.items?.map((t) => (
                <option key={t.slug} value={t.slug}>{t.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[11.5px] font-semibold text-muted-foreground">Betreff *</label>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Ticket-Betreff"
              className="pp-input w-full"
            />
          </div>
          <div>
            <label className="text-[11.5px] font-semibold text-muted-foreground">Beschreibung</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              placeholder="Details zum Ticket..."
              className="pp-input w-full resize-y"
            />
          </div>
          <div>
            <label className="text-[11.5px] font-semibold text-muted-foreground">Priorität</label>
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value as "LOW" | "MEDIUM" | "HIGH")}
              className="pp-input w-full"
            >
              <option value="LOW">Niedrig</option>
              <option value="MEDIUM">Mittel</option>
              <option value="HIGH">Hoch</option>
            </select>
          </div>
          <div className="flex gap-2 justify-end mt-2">
            <button onClick={onClose} className="pp-btn pp-btn-sm" disabled={submitting}>Abbrechen</button>
            <button
              onClick={submit}
              disabled={submitting || !subject.trim()}
              className="inline-flex items-center gap-1 text-[12.5px] font-medium px-3 py-1.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {submitting ? "Erstelle..." : "Erstellen"}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
